// src/pages/Home.tsx
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useI18n } from "../i18n/i18n";

export default function Home() {
  const { lang, setLang, t } = useI18n();

  const IMAGES = useMemo(
    () => [
      "/showcase/realista-1080p.png",
      "/showcase/producto-2k.png",
      "/showcase/campana-4k.png",
      "/showcase/ilustracion-1080p.png",
    ],
    []
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!IMAGES.length) return;
    const timer = window.setInterval(() => {
      setIndex((i) => (i + 1) % IMAGES.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [IMAGES.length]);

  useEffect(() => {
    IMAGES.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [IMAGES]);

  // ✅ estilos consistentes para header (premium, rápido)
  const navLinkClass =
    "px-3 py-2 rounded-lg text-sm font-medium text-white/75 " +
    "hover:text-white hover:bg-white/5 transition " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40";

  const headerBtnBase =
    "h-10 inline-flex items-center justify-center rounded-xl px-4 text-sm font-semibold transition " +
    "outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 focus-visible:ring-offset-0";

  const headerBtnGhost =
    headerBtnBase +
    " border border-white/12 bg-white/5 text-white/90 hover:bg-white/10 hover:border-white/20";

  // ✅ Unificar tamaño de títulos de secciones
  const sectionTitleClass = "text-2xl md:text-3xl font-semibold leading-tight";

  const langPill =
    "h-10 inline-flex items-center rounded-xl border border-white/12 bg-white/5 p-1";

  const langBtn =
    "h-8 px-3 rounded-lg text-xs font-semibold transition outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40";

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white flex flex-col overflow-hidden">
      {/* ─────────── NAVBAR (FIXED) ─────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0b0b0f]/70 backdrop-blur-md">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-indigo-500/10 via-transparent to-transparent" />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <div className="flex justify-between items-center py-3.5">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-baseline gap-2 group">
                <span className="text-2xl font-bold tracking-tight leading-none group-hover:text-white transition">
                  Ovylon
                </span>
                <span className="text-[11px] text-gray-400 font-semibold uppercase tracking-[0.18em] leading-none">
                  AI Studio
                </span>
              </Link>

              {/* ✅ Nav más abajo para alinear con logo */}
              <nav className="hidden md:flex items-center gap-1 mt-[6px]">
                <Link
                  to="/dashboard?tab=ia"
                  className={navLinkClass}
                  title={t("home.nav.openCreatorTitle")}
                >
                  {t("home.nav.creator")}
                </Link>
                <Link
                  to="/brand/create"
                  className={navLinkClass}
                  title={t("home.nav.openBrandTitle")}
                >
                  {t("home.nav.brand")}
                </Link>
                <Link to="/about" className={navLinkClass}>
                  {t("home.nav.howItWorks")}
                </Link>
                <Link to="/pricing" className={navLinkClass}>
                  {t("home.nav.pricing")}
                </Link>
              </nav>
            </div>

            {/* ✅ Acciones: idioma + CTA */}
            <div className="hidden md:flex items-center gap-2">
              {/* Toggle idioma */}
              <div className={langPill} aria-label={t("common.language")}>
                <button
                  type="button"
                  onClick={() => setLang("es")}
                  className={`${langBtn} ${
                    lang === "es"
                      ? "bg-white/12 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/8"
                  }`}
                >
                  {t("common.es")}
                </button>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`${langBtn} ${
                    lang === "en"
                      ? "bg-white/12 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/8"
                  }`}
                >
                  {t("common.en")}
                </button>
              </div>

              <Link to="/login" className={headerBtnGhost}>
                {t("home.nav.login")}
              </Link>

              <Link
                to="/signup"
                className={
                  headerBtnBase +
                  " bg-indigo-500 text-white hover:bg-indigo-400 " +
                  "shadow-lg shadow-indigo-500/25"
                }
              >
                {t("home.nav.signup")}
              </Link>
            </div>

            {/* ✅ Mobile actions */}
            <div className="md:hidden flex items-center gap-2">
              {/* Toggle idioma compacto */}
              <div className={langPill} aria-label={t("common.language")}>
                <button
                  type="button"
                  onClick={() => setLang("es")}
                  className={`${langBtn} ${
                    lang === "es"
                      ? "bg-white/12 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/8"
                  }`}
                >
                  {t("common.es")}
                </button>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`${langBtn} ${
                    lang === "en"
                      ? "bg-white/12 text-white"
                      : "text-white/70 hover:text-white hover:bg-white/8"
                  }`}
                >
                  {t("common.en")}
                </button>
              </div>

              <Link to="/login" className={headerBtnGhost}>
                {t("home.nav.mobileLogin")}
              </Link>

              <Link
                to="/signup"
                className={
                  "h-10 inline-flex items-center justify-center rounded-xl px-4 text-sm font-semibold transition " +
                  "bg-indigo-500 text-white hover:bg-indigo-400 shadow-md shadow-indigo-500/25 " +
                  "focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                }
              >
                {t("home.nav.mobileSignup")}
              </Link>
            </div>
          </div>

          {/* ✅ Mobile quick links */}
          <div className="md:hidden pb-4">
            <div className="flex items-center gap-2">
              <Link
                to="/dashboard?tab=ia"
                className="flex-1 h-10 inline-flex items-center justify-center rounded-xl text-sm font-semibold border border-white/10 bg-white/5 hover:bg-white/10 transition text-center"
              >
                {t("home.nav.creator")}
              </Link>
              <Link
                to="/brand/create"
                className="flex-1 h-10 inline-flex items-center justify-center rounded-xl text-sm font-semibold border border-white/10 bg-white/5 hover:bg-white/10 transition text-center"
              >
                {t("home.nav.brand")}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* ✅ Espacio para compensar header fixed */}
      <div className="h-[76px] md:h-[72px]" />

      {/* ─────────── HERO + SLIDER ─────────── */}
      <main className="relative flex flex-col items-center text-center mt-10 md:mt-14 px-6">
        {/* Glow background */}
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-500/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl" />

        <h1 className="text-4xl md:text-5xl font-bold max-w-4xl leading-tight">
          {t("home.hero.titleLine1")}
          <br />
          <span className="text-indigo-400">{t("home.hero.titleEmphasis")}</span>
        </h1>

        <p className="text-gray-400 mt-5 md:mt-6 max-w-2xl text-base md:text-lg">
          {t("home.hero.subtitleLine1")}
          <br />
          {t("home.hero.subtitleLine2")}
        </p>

        {/* CTA refinados */}
        <div className="mt-8 md:mt-10 flex gap-3 flex-wrap justify-center">
          <Link
            to="/signup"
            className="min-w-[180px] px-6 md:px-7 py-3 md:py-3.5 bg-white text-black rounded-xl text-sm md:text-base font-semibold hover:bg-gray-200 transition text-center"
          >
            {t("home.hero.ctaPrimary")}
          </Link>

          <Link
            to="/login"
            className="min-w-[180px] px-6 md:px-7 py-3 md:py-3.5 border border-white/20 rounded-xl text-sm md:text-base font-semibold hover:bg-white/10 transition text-center"
          >
            {t("home.hero.ctaSecondary")}
          </Link>
        </div>

        <p className="mt-4 text-sm text-gray-500">{t("home.hero.note")}</p>

        {/* Slider */}
        <section className="w-full max-w-6xl mt-12 md:mt-16">
          <div className="relative mx-auto w-full overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-2xl">
            <div className="relative w-full h-[420px] sm:h-[520px] md:h-[520px] lg:h-[560px]">
              <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-black/10 via-black/10 to-black/45" />
              <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-r from-black/25 via-transparent to-black/25" />

              {IMAGES.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={t("home.showcase.alt")}
                  className={[
                    "absolute inset-0 w-full h-full object-cover",
                    "transition-opacity duration-1000 ease-in-out",
                    i === index ? "opacity-100" : "opacity-0",
                  ].join(" ")}
                  loading={i === 0 ? "eager" : "lazy"}
                  draggable={false}
                />
              ))}

              <div className="absolute z-20 bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {IMAGES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={[
                      "h-2.5 rounded-full transition-all",
                      i === index ? "w-8 bg-white/80" : "w-2.5 bg-white/30 hover:bg-white/50",
                    ].join(" ")}
                    aria-label={t("home.showcase.dotAria", `View image ${i + 1}`).replace("{n}", String(i + 1))}
                  />
                ))}
              </div>

              <div className="absolute z-20 top-4 left-4 md:top-5 md:left-5">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-black/30 text-xs text-gray-200">
                  <span className="h-2 w-2 rounded-full bg-indigo-400" />
                  {t("home.showcase.badge")}
                </div>
              </div>
            </div>
          </div>

          <p className="mt-3 text-xs text-gray-500">{t("home.showcase.caption")}</p>
        </section>
      </main>

      {/* ─────────── DIFFERENTIATORS + EXPERIENCES ─────────── */}
      <section className="mt-14 md:mt-18 px-6 md:px-10 max-w-6xl mx-auto">
        <div className="text-center">
          <h2 className={`mt-4 ${sectionTitleClass}`}>
            {t("home.diff.titleLine1")}
            <br />
            <span className="text-indigo-300">{t("home.diff.titleEmphasis")}</span>
          </h2>

          <p className="mt-3 text-sm md:text-base text-gray-400 max-w-3xl mx-auto">
            {t("home.diff.subtitle")}
          </p>
        </div>

        {/* Pillars */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">{t("home.pillars.p1.title")}</h3>
                <p className="mt-1 text-sm text-gray-400">{t("home.pillars.p1.desc")}</p>
              </div>
              <span className="shrink-0 rounded-xl border border-white/10 bg-black/30 px-2.5 py-1 text-xs text-gray-200">
                {t("home.pillars.p1.tag")}
              </span>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="pointer-events-none absolute -top-24 -left-24 h-56 w-56 rounded-full bg-purple-500/10 blur-3xl" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">{t("home.pillars.p2.title")}</h3>
                <p className="mt-1 text-sm text-gray-400">{t("home.pillars.p2.desc")}</p>
              </div>
              <span className="shrink-0 rounded-xl border border-white/10 bg-black/30 px-2.5 py-1 text-xs text-gray-200">
                {t("home.pillars.p2.tag")}
              </span>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6">
            <div className="pointer-events-none absolute -bottom-24 -right-24 h-56 w-56 rounded-full bg-indigo-500/10 blur-3xl" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">{t("home.pillars.p3.title")}</h3>
                <p className="mt-1 text-sm text-gray-400">{t("home.pillars.p3.desc")}</p>
              </div>
              <span className="shrink-0 rounded-xl border border-white/10 bg-black/30 px-2.5 py-1 text-xs text-gray-200">
                {t("home.pillars.p3.tag")}
              </span>
            </div>
          </div>
        </div>

        {/* Divider 1 */}
        <div className="mt-10 flex items-center justify-center">
          <div className="h-px w-24 bg-white/10" />
          <div className="mx-3 h-2 w-2 rounded-full bg-indigo-400/60" />
          <div className="h-px w-24 bg-white/10" />
        </div>

        {/* Title 2 (mismo tamaño) */}
        <div className="mt-10 text-center">
          <h3 className={sectionTitleClass}>
            {t("home.exp.titleLine1")}
            <br />
            <span className="text-indigo-300">{t("home.exp.titleEmphasis")}</span>
          </h3>

          <p className="mt-3 text-sm md:text-base text-gray-400 max-w-3xl mx-auto">
            {t("home.exp.subtitle")}
          </p>
        </div>

        {/* Experiences */}
        <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Creators */}
          <div className="relative overflow-hidden bg-white/5 p-8 rounded-2xl border border-white/10 backdrop-blur-sm">
            <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/5 blur-3xl" />

            <div>
              <p className="text-xs text-gray-300 border border-white/10 bg-black/20 inline-flex px-3 py-1.5 rounded-full">
                {t("home.exp.creators.pill")}
              </p>
              <h3 className="mt-3 text-xl font-semibold">{t("home.exp.creators.title")}</h3>
              <p className="mt-2 text-gray-400 text-sm">{t("home.exp.creators.desc")}</p>
            </div>

            <ul className="mt-5 text-sm text-gray-300 space-y-2">
              {[
                t("home.exp.creators.b1"),
                t("home.exp.creators.b2"),
                t("home.exp.creators.b3"),
                t("home.exp.creators.b4"),
                t("home.exp.creators.b5"),
                t("home.exp.creators.b6"),
              ].map((txt) => (
                <li key={txt} className="flex gap-2">
                  <span className="text-indigo-300">•</span> {txt}
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="px-5 py-2.5 bg-white text-black rounded-xl text-sm font-semibold hover:bg-gray-200 transition"
              >
                {t("home.exp.creators.ctaPrimary")}
              </Link>
              <Link
                to="/about"
                className="px-5 py-2.5 border border-white/15 rounded-xl text-sm font-semibold hover:bg-white/10 transition"
              >
                {t("home.exp.creators.ctaSecondary")}
              </Link>
            </div>
          </div>

          {/* Brands */}
          <div className="relative overflow-hidden bg-indigo-500/10 p-8 rounded-2xl border border-indigo-400/30 backdrop-blur-sm">
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-indigo-500/15 blur-3xl" />

            <div>
              <p className="text-xs text-indigo-100 border border-indigo-400/30 bg-indigo-500/10 inline-flex px-3 py-1.5 rounded-full">
                {t("home.exp.brands.pill")}
              </p>
              <h3 className="mt-3 text-xl font-semibold">{t("home.exp.brands.title")}</h3>
              <p className="mt-2 text-gray-200/80 text-sm">{t("home.exp.brands.desc")}</p>
            </div>

            <ul className="mt-5 text-sm text-gray-200 space-y-2">
              {[
                t("home.exp.brands.b1"),
                t("home.exp.brands.b2"),
                t("home.exp.brands.b3"),
                t("home.exp.brands.b4"),
                t("home.exp.brands.b5"),
                t("home.exp.brands.b6"),
              ].map((txt) => (
                <li key={txt} className="flex gap-2">
                  <span className="text-indigo-200">•</span> {txt}
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/signup"
                className="px-5 py-2.5 bg-white text-black rounded-xl text-sm font-semibold hover:bg-gray-200 transition"
              >
                {t("home.exp.brands.ctaPrimary")}
              </Link>
              <Link
                to="/about"
                className="px-5 py-2.5 border border-indigo-300/25 rounded-xl text-sm font-semibold hover:bg-white/10 transition"
              >
                {t("home.exp.brands.ctaSecondary")}
              </Link>
            </div>
          </div>
        </div>

        {/* ✅ Divider extra */}
        <div className="mt-12 flex items-center justify-center">
          <div className="h-px w-28 bg-white/10" />
          <div className="mx-3 h-2 w-2 rounded-full bg-indigo-400/60" />
          <div className="h-px w-28 bg-white/10" />
        </div>

        {/* ─────────── COMPARATIVO ─────────── */}
        <div className="mt-12 md:mt-14">
          <div className="text-center mb-5 md:mb-6">
            <h4 className={sectionTitleClass}>
              {t("home.compare.titleLine1")}
              <br />
              <span className="text-indigo-300">{t("home.compare.titleEmphasis")}</span>
            </h4>

            <p className="mt-3 text-sm md:text-base text-gray-400 max-w-4xl mx-auto leading-relaxed">
              {t("home.compare.subtitle")}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
            <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl" />

            <div className="overflow-hidden rounded-xl border border-white/10">
              <div className="grid grid-cols-3 bg-black/30 text-sm">
                <div className="px-4 py-3 text-gray-300">{t("home.compare.table.feature")}</div>
                <div className="px-4 py-3 font-semibold text-center">{t("home.compare.table.ovylon")}</div>
                <div className="px-4 py-3 text-gray-300 text-center">{t("home.compare.table.others")}</div>
              </div>

              {[
                { key: "r1", ov: "✔", other: t("home.compare.other.no") },
                { key: "r2", ov: "✔", other: t("home.compare.other.sometimes") },
                { key: "r3", ov: "✔", other: t("home.compare.other.depends") },
                { key: "r4", ov: "✔", other: t("home.compare.other.no") },
                { key: "r5", ov: "✔", other: t("home.compare.other.no") },
                { key: "r6", ov: "✔", other: t("home.compare.other.no") },
                { key: "r7", ov: "✔", other: t("home.compare.other.sometimes") },
                { key: "r8", ov: "✔", other: t("home.compare.other.no") },
              ].map((row) => {
                const label = t(`home.compare.rows.${row.key}`);
                return (
                  <div key={row.key} className="grid grid-cols-3 border-t border-white/10 bg-white/0 text-sm">
                    <div className="px-4 py-3 text-gray-300">{label}</div>
                    <div className="px-4 py-3 font-semibold text-white text-center">{row.ov}</div>
                    <div className="px-4 py-3 font-semibold text-center">
                      {row.other === t("home.compare.other.no") ? (
                        <span className="text-red-400">✕</span>
                      ) : (
                        <span className="text-gray-400">{row.other}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-3 text-xs text-gray-500">{t("home.compare.note")}</p>
          </div>

          {/* Botones fuera del cuadro */}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/signup"
              className="min-w-[190px] px-6 py-3 bg-white text-black rounded-xl text-sm font-semibold hover:bg-gray-200 transition text-center"
            >
              {t("home.hero.ctaPrimary")}
            </Link>
            <Link
              to="/login"
              className="min-w-[190px] px-6 py-3 border border-white/15 rounded-xl text-sm font-semibold hover:bg-white/10 transition text-center"
            >
              {t("home.hero.ctaSecondary")}
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────── FOOTER (LINKS) ─────────── */}
      <footer className="mt-20 md:mt-28 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div>
            © {new Date().getFullYear()} Ovylon — {t("home.footer.rights")}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/about" className="hover:text-gray-300 transition">
              {t("home.footer.about")}
            </Link>
            <Link to="/support" className="hover:text-gray-300 transition">
              {t("home.footer.support")}
            </Link>
            <Link to="/privacy" className="hover:text-gray-300 transition">
              {t("home.footer.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-gray-300 transition">
              {t("home.footer.terms")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}






